import {ChatHistory} from "./types";
import React from "react";
import {useChatService} from "../../services";
import {useDispatch} from "react-redux";
import {updateChatTitle} from "../../redux/slices/chat";
import {HiCheck, HiX} from "react-icons/hi";

interface ChatEditInputProps {
  history: ChatHistory;
  inputRefs: any;
  showEditInput: Record<number, boolean>;
  onToggleEditInput: (record: Record<number, boolean>) => void;
}

const ChatEditInput: React.FC<ChatEditInputProps> = ({ history, inputRefs, showEditInput, onToggleEditInput }) => {
  const { saveChatTitleById } = useChatService();
  const dispatch = useDispatch();

  const handleInputSaveClick = async (chat_id: number) => {
    const inputElement = inputRefs.current[chat_id];
    const getValue = inputElement?.value?.trim();
    const editInputObj: Record<number, boolean> = { ...showEditInput };
    editInputObj[chat_id] = false;

    if (chat_id && getValue) {
      try {
        // Update Redux state immediately to reflect the change in UI
        dispatch(updateChatTitle({ chat_id, title: getValue }));
        onToggleEditInput(editInputObj);

        await saveChatTitleById(chat_id, { title: getValue });
      } catch (error) {
        console.error('Failed to save chat title:', error);
        // Keep the input in edit mode if save failed
        editInputObj[chat_id] = true;
        onToggleEditInput(editInputObj);
      }
    } else {
      // If no title provided, just cancel editing
      onToggleEditInput(editInputObj);
    }
  };

  const handleCancelEdit = (chat_id: number) => {
    const editInputObj: Record<number, boolean> = { ...showEditInput };
    editInputObj[chat_id] = false;
    onToggleEditInput(editInputObj);
  };

  const handleEditKeyDown = async (e: React.KeyboardEvent, chat_id: number) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      await handleInputSaveClick(chat_id);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelEdit(chat_id);
    }
  };

  return (
    <div className="flex items-center gap-1 w-full">
      <input
        id={history.chat_id.toString()}
        className="p-1 pl-1 flex-1 text-xs rounded-md bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
        type="text"
        ref={(el) => (inputRefs.current[history.chat_id] = el)}
        defaultValue={history.title}
        onKeyDown={(e) => handleEditKeyDown(e, history.chat_id)}
        placeholder="Enter chat title..."
        onClick={(e) => e.stopPropagation()} // Prevent triggering chat selection when clicking input
      />
      
      {/* Accept (Tick) Button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          handleInputSaveClick(history.chat_id);
        }}
        className="flex-shrink-0 p-1 hover:bg-green-100 dark:hover:bg-green-900/30 rounded text-green-600 dark:text-green-400 transition-colors"
        title="Save changes"
      >
        <HiCheck className="w-4 h-4" />
      </button>
      
      {/* Reject (Cross) Button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          handleCancelEdit(history.chat_id);
        }}
        className="flex-shrink-0 p-1 hover:bg-red-100 dark:hover:bg-red-900/30 rounded text-red-600 dark:text-red-400 transition-colors"
        title="Cancel editing"
      >
        <HiX className="w-4 h-4" />
      </button>
    </div>
  )
}

export default ChatEditInput;